import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Lightweight i18n provider — Map-based dictionary, persisted locale,
 * `useT()` hook for translations and a `useLocale()` hook for the
 * picker. Deliberately not pulling in i18next: the Connect surface is
 * small enough that a 5 KB hand-rolled module beats a 50 KB framework
 * with plural-forms infrastructure we don't yet need. Adding more
 * locales is one more entry per dictionary file.
 *
 * Translation keys are dotted strings (`compose.send`, `meeting.end`).
 * Missing keys fall back to the English entry; a missing English entry
 * falls back to the key itself so the UI never blanks out.
 */

const STORAGE_KEY = "fg.connect.locale";

export type LocaleId = "en" | "hi";

export const SUPPORTED_LOCALES: ReadonlyArray<{ id: LocaleId; label: string; nativeLabel: string }> = [
    { id: "en", label: "English", nativeLabel: "English" },
    { id: "hi", label: "Hindi", nativeLabel: "हिंदी" }
];

type Dictionary = Readonly<Record<string, string>>;

const en: Dictionary = {
    "app.title": "Meeting",
    "app.signOut": "Sign out",
    "app.darkMode": "Dark mode",
    "app.lightMode": "Light mode",
    "app.help": "Help",
    "app.settings": "Settings",
    "app.notifications": "Notifications",
    "app.search.placeholder": "Search messages, people, meetings",
    "app.newMeeting": "New meeting",
    "app.quietHours": "Quiet hours…",
    "app.language": "Language",
    "app.install": "Install app",

    "mode.channels": "Channels",
    "mode.chats": "Chats",
    "mode.groups": "Groups",
    "mode.calendar": "Calendar",
    "mode.history": "Call history",
    "mode.saved": "Saved",

    "compose.placeholder": "Type a message",
    "compose.send": "Send",
    "compose.attach": "Attach file",

    "presence.online": "Available",
    "presence.busy": "Busy",
    "presence.dnd": "Do not disturb",
    "presence.brb": "Be right back",
    "presence.away": "Away"
};

const hi: Dictionary = {
    "app.title": "अथविता कनेक्ट",
    "app.signOut": "साइन आउट",
    "app.darkMode": "डार्क मोड",
    "app.lightMode": "लाइट मोड",
    "app.help": "मदद",
    "app.settings": "सेटिंग्स",
    "app.notifications": "सूचनाएँ",
    "app.search.placeholder": "संदेश, लोग, मीटिंग खोजें",
    "app.newMeeting": "नई मीटिंग",
    "app.quietHours": "शांत समय…",
    "app.language": "भाषा",
    "app.install": "ऐप इंस्टॉल करें",

    "mode.channels": "चैनल",
    "mode.chats": "चैट",
    "mode.groups": "समूह",
    "mode.calendar": "कैलेंडर",
    "mode.history": "कॉल इतिहास",
    "mode.saved": "सहेजे गए",

    "compose.placeholder": "संदेश लिखें",
    "compose.send": "भेजें",
    "compose.attach": "फ़ाइल संलग्न करें",

    "presence.online": "उपलब्ध",
    "presence.busy": "व्यस्त",
    "presence.dnd": "परेशान न करें",
    "presence.brb": "अभी लौटा",
    "presence.away": "दूर"
};

const DICTIONARIES: Readonly<Record<LocaleId, Dictionary>> = { en, hi };

interface I18nContextValue {
    locale: LocaleId;
    setLocale: (l: LocaleId) => void;
    t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitialLocale(): LocaleId {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === "en" || stored === "hi") return stored;
    } catch { /* private mode / SSR */ }
    const lang = (typeof navigator !== "undefined" && navigator.language) ? navigator.language.toLowerCase() : "en";
    if (lang.startsWith("hi")) return "hi";
    return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<LocaleId>(() => detectInitialLocale());

    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
        try { document.documentElement.setAttribute("lang", locale); } catch { /* ignore */ }
    }, [locale]);

    const setLocale = useCallback((l: LocaleId) => setLocaleState(l), []);

    const t = useCallback((key: string, fallback?: string): string => {
        const dict = DICTIONARIES[locale];
        if (key in dict) return dict[key]!;
        if (key in DICTIONARIES.en) return DICTIONARIES.en[key]!;
        return fallback ?? key;
    }, [locale]);

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): (key: string, fallback?: string) => string {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        // Safe fallback when used outside the provider — return the key
        // so the UI still renders. This keeps individual components from
        // crashing during testing or storybook contexts.
        return (key, fallback) => fallback ?? key;
    }
    return ctx.t;
}

export function useLocale(): { locale: LocaleId; setLocale: (l: LocaleId) => void } {
    const ctx = useContext(I18nContext);
    if (!ctx) return { locale: "en", setLocale: () => { /* no-op */ } };
    return { locale: ctx.locale, setLocale: ctx.setLocale };
}
